<template>
    <div>
        <div
            ref="dropdowns"
            class="dropdowns"
            tabindex="-1"
        >
            <dropdowns
                :key="forceUpdateKey"
                list-heading="App.RadioButtonListHeader.ViatJaHairiot.ValitseAjanjakso"
                :dropdown-list="dropdownListTimeLine"
                :default-item="dropdownListTimeLine[0].identifier"
                class="dropdowns"
                @selected-dropdown="handleSelectedDropdownTimeLine"
            />
        </div>
        <area-lists
            list-heading="App.RadioButtonListHeader.ValitseAlue"
            :visible-area-types="visibleAreaTypes"
            :selected-area-type="defaultAreaType"
            @selected-radio-button="handleAreaSelectionRadioButton"
            @selected-list-value="handleAreaSelectionListValue"
        />
    </div>
</template>

<script lang="ts">
    import { Component, Vue, Watch } from "vue-facing-decorator"
    import DropdownList from "@/components/DropdownList.vue"
    import DropDownListModel from "@/models/dropdownlistmodel"
    import DynamicRightSlotData from "@/models/dynamicrighslotdata"
    import RadioButtonModel from "@/models/radiobuttonmodel"
    import MunicipalitiesAndProvinces from "@/components/MunicipalitiesAndProvinces.vue"
    import { AreaTypes } from "@/helpers/enums"
    import MunicipalitiesAndRegionsModel from "@/models/municipalitiesandregionsmodel"
    import { store } from '@/store/store'

    @Component({
        components: {
            dropdowns: DropdownList,
            "area-lists": MunicipalitiesAndProvinces
        }
    })
    export default class ViatJaHairiot extends Vue {
        // DataPublishServices rajapintakutsun etuosa
        apiCallBasePart: string = "v1/Viestinta/viatjahairiot/"
        selectedFilters : Map<string, string> = new Map<string, string>([
            ["Timeline", "Viimeisin12kk"],
            ["Area", "kokosuomi"]
        ])

        defaultAreaType: AreaTypes = AreaTypes.Province
        visibleAreaTypes: Array<AreaTypes> = [AreaTypes.Province, AreaTypes.Country]
        selectedTimeLine: string = "Viimeisin12kk"

        pageName: string = "viatjahairiot"

        forceUpdateKey = 0

        async activated() {
            store.dispatch("setCurrentPage", this.pageName)
            this.setTimeTitle(this.selectedTimeLine)
            this.forceUpdateKey += 1
            await this.setDataToLoad()
        }

        deactivated() {
            this.selectedTimeLine = "Viimeisin12kk"
            store.dispatch("setCurrentPage", "")
            store.dispatch("setSelectedDropDownResourceKey", "")
            store.dispatch("setSelectedAreaRadioButton", "")
            store.dispatch("setSelectedProvince", "")
        }

        async handleSelectedDropdownTimeLine(item: string) {
            this.setRightSlotFilters("Timeline", item)
            this.selectedTimeLine = item
            this.setTimeTitle(item)
            await this.setDataToLoad()
        }

        async setTimeTitle(title: string) {
            // asetaan aika näkymää rightslotin otsikoissa
            if (this.selectedTimeLine === "Viimeisin12kk") {
                title = this.$t("App.ViatJaHairiot." + this.selectedTimeLine) ?? ""
            }
            store.dispatch("setSelectedDropDownResourceKey", title)
        }

        async handleAreaSelectionRadioButton(item: RadioButtonModel) {
            store.dispatch("setSelectedAreaRadioButton", String(item))
            await this.setDataToLoad()
        }

        async handleAreaSelectionListValue(item: string) {
            // Valittu maakunta dropdownista
            if (store.getters.selectedAreaRadioButton === "maakunta") {
                store.dispatch("setSelectedProvince", item)
                this.setRightSlotFilters("Area", item)
                await this.setDataToLoad()
            }
        }

        async setDataToLoad(): Promise<void> {
            if (this.currentPage !== this.pageName) {
                return
            }

            // todo tämä pitäisi olla yleisesti kutsuttavissa mm kartalta tai rightslotista eli kaikki hakuparametrit pitäisi olla koko ajan muistissa.
            store.dispatch("setLoading", { name: "viatjahairiot", value: true })

            // Asetetaan oikean slotin sisältö
            store.dispatch("setRightSlot", "ViatJaHairiotTiedot")
            // Viedään rightslotDataan valinnat
            store.dispatch("setRightSlotData", await this.getFilters())

            const filters = new URLSearchParams()

            const dates = this.getTimelineToDates(this.selectedTimeLine)
            filters.append("alkuaika", dates[0])
            filters.append("loppuaika", dates[1])
            const selectedAreaRadioButton = store.getters.selectedAreaRadioButton
            if (selectedAreaRadioButton === "maakunta") filters.append("maakuntanumero", store.getters.selectedProvince)

            const clearLocationMarker = store.getters.currentPage.toLowerCase() !== store.getters.dataUrlToLoad?.url?.split("/")[1]?.toLowerCase()

            store.dispatch("setDataUrlToLoad", { url: this.apiCallBasePart + "ViatJaHairiot", type: "ViatJaHairiot", params: filters, clearLocationMarker })

            store.dispatch("setLoading", { name: "viatjahairiot", value: false })
        }

        getTimelineToDates(timeline: string): Array<string> {
            const dates: Array<string> = []
            if (timeline === "Viimeisin12kk") {
                let date = new Date()
                date = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
                date.setMonth(date.getMonth() - 12)
                dates.push(date.toISOString()) // alkuaika = nykyhetki - 12 kk
                dates.push(new Date().toISOString()) // loppuaika = nykyhetki
            }
            else {
                const year: number = +timeline
                dates.push(new Date(Date.UTC(year, 0, 1)).toISOString()) // alkuaika = valitun vuoden eka päivä, huom kuukaudet alkavat nollasta
                dates.push(new Date(Date.UTC(year, 11, 31, 23, 59)).toISOString()) // loppuaika = valitun vuoden viimeine päivä, huom kuukaudet alkavat nollasta
            }
            return dates
        }

        setRightSlotFilters(filter: string, value: string): void {
            this.selectedFilters.set(filter, value)
        }

        get currentPage(): string {
            return store.getters.currentPage
        }

        get activeLang() {
            return store.getters.activeLang
        }

        @Watch("activeLang")
        async onActiveLang(): Promise<void> {
            // Kielivalinta muuttunut. Ladataan data uudestaan.
            if (this.currentPage !== this.pageName) {
                return
            }
            this.setDataToLoad()
        }

        getRegions(): Array<MunicipalitiesAndRegionsModel> {
            return store.getters.regionsData
        }

        async getFilters(): Promise<DynamicRightSlotData> {
            const rightSlotData = new DynamicRightSlotData("viat")
            const regions = this.getRegions()
            const selectedAreaRadioButton = store.getters.selectedAreaRadioButton
            if (selectedAreaRadioButton === "maakunta") {
                // haetaan maakunnan nimi
                const maakunta = regions.find(a => a.maakuntanumero === store.getters.selectedProvince)
                if (this.activeLang !== "sv") {
                    rightSlotData.municipality = maakunta?.nimi ?? ""
                }
                else {
                    rightSlotData.municipality = maakunta?.namn ?? ""
                }
            }
            else {
                rightSlotData.municipality = this.$t("App.RadioButtonList.EntireFinland") ?? "App.RadioButtonList.EntireFinland"
            }
            rightSlotData.filters = this.selectedFilters
            return rightSlotData
        }

        updated() {
            this.$nextTick(() => {
                (this.$refs.dropdowns as HTMLHeadingElement).focus()
            })
        }

        get leftSlotHeaderViatJaHairiot(): string | undefined {
            return this.$t("App.LeftSlot.Header.ViatJaHairiot")
        }

        get dropdownListTimeLine(): Array<DropDownListModel> {
            // TODO ajanjaksojen haku, mitä jaksoja näytetään? viimeisin 12kk ja aikaisemmat vuodet? eli mitä? 10v taaksepäin? vai joku alku vuosi ja siit nykyvuoteen?
            const years: Array<DropDownListModel> = []
            years.push(new DropDownListModel(this.$t("App.ViatJaHairiot.Viimeisin12kk") ?? "App.ViatJaHairiot.Viimeisin12kk", "Viimeisin12kk", false))
            for (let i = new Date().getFullYear(); i >= 2012; i--) {
                years.push(new DropDownListModel(i.toString(), i.toString(), false))
            }
            return years
        }
    }
</script>
<style scoped>
    .dropdowns {
       margin-bottom: 2.5rem;
    }
</style>
